/**
 * One screening question as the person answers it: the portal's own choices
 * as chips, a dropdown or a filtered list, a yes/no, a date, a number, or
 * text with "Write with AI". Shared by the per-application review page and
 * the review inbox (every waiting application's questions in one place).
 */
import { useLayoutEffect, useRef, useState, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { ApiError } from '@/api/client'
import { applicationApi } from '@/api/endpoints'
import { Combobox } from '@/components/ui/Select'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { AnswerAssistMode, AnswerSource, ScreeningQuestion } from '@/types/api'
import { DocumentUploadField, documentKindOf } from './DocumentUploadField'

/** What the field needs of a question: the review page's rows and the inbox's groups both have it. */
export type QuestionLike = Pick<
  ScreeningQuestion,
  'questionKey' | 'questionText' | 'questionType' | 'options' | 'isRequired' | 'answerValue' | 'answerSource' | 'rationale' | 'suggestedValue' | 'tip' | 'optionsComplete' | 'documentKind'
>

export const SOURCE_LABEL: Record<AnswerSource, string> = {
  profile: 'Profile',
  previous_answer: 'Remembered',
  ai: 'AI',
  user: 'You',
}

export const LONG_ANSWER_CHARS = 200
export const LONG_QUESTION = /why|describe|tell us|explain|brief/i
/** The rationale the backend leaves on an answer the employer's site refused (`applicationQueries.refusalNote`). */
export const REFUSAL_NOTE = /\bdid not accept this answer:/i
/** More options than this are a searchable list, not a native dropdown. */
export const LONG_LIST = 15

export function QuestionField({
  applicationId,
  question,
  value,
  onChange,
  showSource,
  assist: assistWith,
}: {
  /** The application the AI drafts against (the per-application review page). */
  applicationId?: string
  question: QuestionLike
  value: string
  onChange: (value: string) => void
  showSource?: boolean
  /** How "Write with AI" is answered when there is no single application (the review inbox). */
  assist?: (mode: AnswerAssistMode, text: string | undefined) => Promise<{ text: string }>
}) {
  const id = `q-${question.questionKey}`
  const hasValue = value.trim() !== ''
  // One level of undo for AI writes: what the field held before the last one.
  const [previous, setPrevious] = useState<string | null>(null)
  // Sticky: once an answer is paragraph-sized it keeps the textarea, so the
  // field never swaps element (and loses focus) mid-typing.
  const [grown, setGrown] = useState(() => (question.answerValue ?? '').length > LONG_ANSWER_CHARS)
  const assist = useMutation({
    mutationFn: (mode: AnswerAssistMode) =>
      assistWith
        ? assistWith(mode, mode === 'improve' ? value : undefined)
        : applicationApi.assistAnswer(applicationId!, {
            questionKey: question.questionKey,
            mode,
            text: mode === 'improve' ? value : undefined,
          }),
    onSuccess: (result) => {
      const text = result?.text?.trim()
      if (!text) {
        toast.error('No answer came back', 'Try again, or write this one yourself.')
        return
      }
      setPrevious(value)
      if (text.length > LONG_ANSWER_CHARS) setGrown(true)
      onChange(text)
    },
    onError: (error: ApiError) => toast.error('Could not write an answer', error.message),
  })
  const edited = value !== (question.answerValue ?? '')
  // A blank option is a reader miss, never a choice to draw (a radio with no words).
  const listed = question.options.filter((option) => option.trim())
  const choices =
    listed.length > 0
      ? listed
      : question.questionType === 'boolean'
        ? ['Yes', 'No']
        : []
  // The portal's list is a part of the whole (a searchable box, a list cut short): the person may type what is not on it.
  const partial = question.optionsComplete === false && choices.length > 0
  const typedOwn = partial && hasValue && !choices.includes(value)
  // Booleans travel as "true"/"false"; everything else as the option text.
  const toWire = (option: string) =>
    question.questionType === 'boolean' && listed.length === 0
      ? option === 'Yes' ? 'true' : 'false'
      : option
  const freeText = question.questionType === 'text' && choices.length === 0
  const long = freeText && (grown || LONG_QUESTION.test(question.questionText))
  const tip = question.tip?.trim()
  // "<site> did not accept this answer: <reason>" — the answer stays; the reason shows until it is changed.
  const refusal = REFUSAL_NOTE.test(question.rationale ?? '') ? question.rationale : null

  const chip = (on: boolean) =>
    cn(
      'flex min-h-[44px] cursor-pointer items-center gap-2 rounded-field border px-3 py-2 text-[14px] transition-colors',
      on ? 'border-ink/40 bg-surface-muted text-ink' : 'border-line text-ink-soft hover:border-line-strong',
    )

  return (
    <div>
      <label htmlFor={id} className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] font-medium leading-snug text-ink">
        <span className="min-w-0 break-words">{question.questionText}</span>
        {question.isRequired ? (
          <span className="rounded-pill border border-line px-1.5 py-px text-[10.5px] font-medium uppercase tracking-wide text-ink-muted">
            Required
          </span>
        ) : (
          <span className="text-[12px] font-normal text-ink-faint">(optional)</span>
        )}
        {question.isRequired && <span className="sr-only"> (required)</span>}
        {showSource && question.answerSource && !edited && (
          <span
            title={question.rationale ?? undefined}
            className={cn(
              'rounded-pill px-2 py-0.5 text-[11px] font-medium',
              question.answerSource === 'ai' ? 'bg-brand-soft text-brand' : 'bg-accent-soft text-ink',
            )}
          >
            {SOURCE_LABEL[question.answerSource]}
          </span>
        )}
      </label>

      {question.questionType === 'file' ? (
        // A paper the form asks for (a photo, an ID proof): uploaded once, attached to this slot — never the résumé.
        <DocumentUploadField id={id} kind={question.documentKind ?? documentKindOf(question.questionText)} value={value} onChange={onChange} required={question.isRequired} />
      ) : choices.length > 0 && choices.length <= 5 && question.questionType !== 'multi_select' ? (
        <div role="radiogroup" aria-labelledby={id} className="flex flex-wrap gap-2">
          {choices.map((option) => (
            <label key={option} className={chip(value === toWire(option))}>
              <input
                type="radio"
                name={id}
                checked={value === toWire(option)}
                onChange={() => onChange(toWire(option))}
                className="h-4 w-4 accent-[rgb(var(--ink))]"
              />
              {option}
            </label>
          ))}
        </div>
      ) : question.questionType === 'multi_select' && choices.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {choices.map((option) => {
            const selected = value.split(',').map((v) => v.trim()).filter(Boolean)
            const on = selected.includes(option)
            return (
              <label key={option} className={chip(on)}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() =>
                    onChange((on ? selected.filter((v) => v !== option) : [...selected, option]).join(', '))
                  }
                  className="h-4 w-4 accent-[rgb(var(--ink))]"
                />
                {option}
              </label>
            )
          })}
        </div>
      ) : choices.length > LONG_LIST ? (
        // A long list (a portal's ~250 countries): type to filter, not an endless native dropdown.
        <Combobox
          id={id}
          appearance="box"
          value={value || null}
          onChange={onChange}
          placeholder="Select…"
          required={question.isRequired}
          options={(value && !choices.includes(value) ? [value, ...choices] : choices).map((option) => ({ value: option, label: option }))}
          emptyMessage="No option matches"
        />
      ) : choices.length > 0 ? (
        <select id={id} value={value} onChange={(event) => onChange(event.target.value)} className="field-box min-h-[44px]">
          <option value="">Select…</option>
          {choices.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : long ? (
        <AutoTextarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={assist.isPending}
          aria-required={question.isRequired}
          aria-busy={assist.isPending || undefined}
        />
      ) : (
        <input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={assist.isPending}
          aria-busy={assist.isPending || undefined}
          type={question.questionType === 'number' ? 'number' : question.questionType === 'date' ? 'date' : 'text'}
          inputMode={question.questionType === 'number' ? 'decimal' : undefined}
          min={question.questionType === 'number' ? 0 : undefined}
          aria-required={question.isRequired}
          className="field-box min-h-[44px] disabled:bg-surface-muted/60 disabled:text-ink-muted"
        />
      )}

      {partial && (
        // The list shown is a part of the employer's: a value typed here is matched on their site.
        <div className="mt-2">
          <label htmlFor={`${id}-own`} className="mb-1 block text-[12.5px] text-ink-muted">
            Not listed? Type it — the form's full list is searched for it.
          </label>
          <input
            id={`${id}-own`}
            value={typedOwn ? value : ''}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Your answer"
            aria-label={`${question.questionText}: a value not listed`}
            className="field-box min-h-[40px]"
          />
        </div>
      )}

      {freeText && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <button
            type="button"
            onClick={() => assist.mutate(hasValue ? 'improve' : 'draft')}
            disabled={assist.isPending}
            className="-ml-2 inline-flex h-8 items-center gap-1.5 rounded-pill px-2 text-[12.5px] font-medium text-brand transition-colors hover:bg-brand-soft disabled:cursor-wait disabled:hover:bg-transparent"
          >
            {assist.isPending ? (
              <>
                <Spinner />
                Writing…
              </>
            ) : (
              <>
                <SparkleIcon />
                {hasValue ? 'Improve with AI' : 'Write with AI'}
              </>
            )}
          </button>
          {previous !== null && previous !== value && !assist.isPending && (
            <button
              type="button"
              onClick={() => {
                onChange(previous)
                setPrevious(null)
              }}
              className="text-[12.5px] text-ink-muted underline decoration-line-strong underline-offset-4 transition-colors hover:text-ink hover:decoration-ink"
            >
              Undo
            </button>
          )}
          {long && (
            <span className="num ml-auto text-[12px] text-ink-faint" aria-live="polite">
              {wordCount(value)} word{wordCount(value) === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {refusal && !edited && (
        // The employer's site refused this answer after Submit: it is kept, and the site's reason shown under it.
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-warning" role="status">
          {refusal}
        </p>
      )}
      {question.suggestedValue && !value && (
        <div className="mt-2 flex max-w-full items-center gap-2 rounded-pill bg-brand-soft py-1 pl-2.5 pr-1 text-[12.5px] sm:inline-flex">
          <SparkleIcon />
          <span className="min-w-0 flex-1 truncate text-ink-soft" title={question.suggestedValue}>
            Suggested: <span className="font-medium text-ink">{question.suggestedValue}</span>
          </span>
          <button
            type="button"
            onClick={() => onChange(question.suggestedValue ?? '')}
            aria-label={`Use suggested answer: ${question.suggestedValue}`}
            className="shrink-0 rounded-pill px-2.5 py-1 font-semibold text-brand transition-colors hover:bg-brand/10"
          >
            Use
          </button>
        </div>
      )}
      {tip ? (
        <p className="mt-2 flex items-start gap-1.5 text-[12.5px] leading-relaxed text-ink-muted">
          <BulbIcon />
          <span className="min-w-0">
            {tip}{' '}
            <ProfileLink />
          </span>
        </p>
      ) : (
        // Why the AI could not answer — only useful until there is an answer.
        !showSource &&
        !hasValue &&
        question.rationale &&
        !question.suggestedValue && <p className="mt-1.5 text-[12px] text-ink-faint">{question.rationale}</p>
      )}
    </div>
  )
}

export function wordCount(text: string) {
  return text.trim() ? text.trim().split(/\s+/).length : 0
}

/** A textarea that grows with its content, from about five lines. */
export function AutoTextarea({ value, className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    // +2 for the borders, which scrollHeight leaves out.
    el.style.height = `${el.scrollHeight + 2}px`
  }, [value])
  return (
    <textarea
      ref={ref}
      rows={5}
      value={value}
      className={cn(
        'field-box block min-h-[140px] resize-none overflow-hidden leading-relaxed disabled:bg-surface-muted/60 disabled:text-ink-muted',
        className,
      )}
      {...props}
    />
  )
}

export function Spinner() {
  return <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
}

export function SparkleIcon() {
  return (
    <svg className="h-3.5 w-3.5 shrink-0 text-brand" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 1.5c.3 2.9 1.6 4.2 4.5 4.5-2.9.3-4.2 1.6-4.5 4.5-.3-2.9-1.6-4.2-4.5-4.5 2.9-.3 4.2-1.6 4.5-4.5ZM12.5 10.5c.15 1.3.7 1.85 2 2-1.3.15-1.85.7-2 2-.15-1.3-.7-1.85-2-2 1.3-.15 1.85-.7 2-2Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function BulbIcon() {
  return (
    <svg className="mt-[3px] h-3.5 w-3.5 shrink-0 text-warning" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M5.2 10.2h3.6M5.6 12.2h2.8M7 1.5a3.7 3.7 0 0 0-2.2 6.7c.4.3.6.7.6 1.2v.8h3.2v-.8c0-.5.2-.9.6-1.2A3.7 3.7 0 0 0 7 1.5Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Profile opens in a new tab: leaving this page would drop the answers typed
 * so far.
 */
export function ProfileLink({ children = 'Update profile' }: { children?: ReactNode }) {
  return (
    <a
      href="/dashboard/profile"
      target="_blank"
      rel="noopener"
      className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-brand underline-offset-4 hover:underline"
    >
      {children}
      <ExternalIcon />
    </a>
  )
}

export function ExternalIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M8 2h4v4M12 2 6.5 7.5M10.5 8.5V12H2V3.5h3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
